// Onboarding clarity — first-run contextual coach hints (tutorial-independent).
//
// Drives the LIVE-match teaching that fires WITHOUT relying on the tutorial:
// when a core system first becomes relevant (you have a token to empower, a
// power-up is usable, ...), a one-shot CoachTip points the player at the now
// self-documenting surface. Tutorial-skippers are first-class — the gate is
// `shouldShowHint` (realMatchesPlayed < ceiling + one-shot per key), NOT
// tutorial completion.
//
// Behavior:
//   - Shows the FIRST eligible candidate (array order = priority); only one tip
//     at a time.
//   - Persists the key to $tutorial.hintsShown the moment it is shown, so it is
//     truly one-shot across sessions/matches (never re-appears).
//   - Auto-hides the visual once the triggering condition resolves (e.g. you
//     empower the piece) so a stale "do X" tip never lingers.
//   - Auto-dismisses after AUTO_DISMISS_MS even if the player never acts, so a
//     tip never sits over gameplay indefinitely (ARCA-131 — AC ≤ 8s).
//   - dismiss() hides the visual immediately (the × on the CoachTip).

import { useStore } from '@nanostores/react'
import { useCallback, useEffect, useState } from 'react'

import { $tutorial, incrementHint } from '../../stores/tutorial'
import { shouldShowHint } from './HintRegistry'

// How long a shown first-run tip stays up before it auto-dismisses if the
// player never interacts. 7s sits safely under the ARCA-131 ≤ 8s bar.
const AUTO_DISMISS_MS = 7000

export interface FirstRunHintCandidate {
  /** One-shot key (persisted to $tutorial.hintsShown). */
  readonly key: string
  /** i18n message key rendered in the CoachTip. */
  readonly copyKey: string
  /** Whether the triggering condition is currently true. */
  readonly active: boolean
}

export interface FirstRunCoachHint {
  readonly key: string
  readonly copyKey: string
}

export interface UseFirstRunCoachHintsReturn {
  readonly activeHint: FirstRunCoachHint | null
  readonly dismiss: () => void
}

export function useFirstRunCoachHints(
  candidates: readonly FirstRunHintCandidate[],
  realMatchesPlayed: number,
): UseFirstRunCoachHintsReturn {
  const tutorial = useStore($tutorial)
  const [shownKey, setShownKey] = useState<string | null>(null)

  // Show + persist the first eligible candidate when nothing is up yet.
  useEffect(() => {
    if (shownKey !== null) return
    const next = candidates.find(
      (c) =>
        c.active &&
        shouldShowHint(c.key, {
          realMatchesPlayed,
          hintsShown: tutorial.hintsShown,
        }),
    )
    if (next) {
      setShownKey(next.key)
      // Persist immediately so it is one-shot even if the player ignores it.
      incrementHint(next.key)
    }
  }, [candidates, realMatchesPlayed, tutorial.hintsShown, shownKey])

  const shownCandidate =
    shownKey !== null
      ? (candidates.find((c) => c.key === shownKey) ?? null)
      : null

  // Auto-hide once the condition resolves (the player acted on it).
  useEffect(() => {
    if (
      shownKey !== null &&
      shownCandidate !== null &&
      !shownCandidate.active
    ) {
      setShownKey(null)
    }
  }, [shownKey, shownCandidate])

  // Auto-dismiss after AUTO_DISMISS_MS so a tip never lingers over gameplay if
  // the player ignores it (ARCA-131 — AC ≤ 8s). Keyed on `shownKey`: a fresh
  // timer is armed each time a new tip becomes shown, and the cleanup clears
  // the pending timer on unmount, manual dismiss, condition-resolve, or tip
  // change — so we never double-dismiss or leak a timer across tips.
  useEffect(() => {
    if (shownKey === null) return
    const timer = setTimeout(() => setShownKey(null), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [shownKey])

  const dismiss = useCallback(() => {
    setShownKey(null)
  }, [])

  const activeHint = shownCandidate?.active
    ? { key: shownCandidate.key, copyKey: shownCandidate.copyKey }
    : null

  return { activeHint, dismiss }
}
